import React, { useState, useEffect, useMemo } from "react";
import {
    Typography,
    FormControl,
    Select,
    MenuItem,
    TextField,
    Grid,
    Box,
    Paper,
    Button,
    makeStyles,
    CircularProgress,
    Snackbar,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import axios from "axios";
import { inputFieldStyles, commonInlineStyles } from "../../styles";
import { appUrl } from "../../../../../../../utils/config";
import { useLocation } from "react-router-dom";

const Alert = (props) => {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
};

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: 0,
    },
    formControl: {
        width: "100%",
    },
    label: {
        marginBottom: theme.spacing(1),
        fontWeight: 900,
    },
    select: {
        backgroundColor: "#fff",
        width: "100%",
    },
    textField: {
        width: "100%",
        backgroundColor: "#fff",
    },
    requiredField: {
        color: theme.palette.error.main,
        marginLeft: theme.spacing(0.5),
    },
    errorText: {
        color: theme.palette.error.main,
        fontSize: 14,
        marginTop: theme.spacing(0.5),
        marginLeft: 0,
    },
    button: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        minWidth: 180,
        // fontWeight: 600,
        // fontSize: 16,
        borderRadius: 30,
        // height: 44,
    },
}));

const OtherConfigurations = ({
    formData,
    updateStepData,
    classes: parentClasses,
    readOnly = false,
    handleNext,
    handleBack,
    handleSubmit, activeStep,
    stepsLength,
    isOtherConfigEditMode = false,
    onValidationChange = () => { },
    markApiCallSuccessful, // Function to mark API call as successful
}) => {
    // Get styles from the custom hook
    const classes = useStyles();
    const location = useLocation()
    const selectedCollabrixID = location.state?.productDetails?.collabrixProductID;
    const [updateModeData, setUpdateModeData] = useState(null);
    const selectedProductFormData = location.state?.selectedProductFormData;
    const addNewProductConfig = location.state?.addNewProductConfig;



    // Initialize step data from formData or with empty values  
    const [stepData, setStepData] = useState(() => {
        const base = formData?.otherConfiguration || {};
        return {
            mroDateFieldCalculation: base?.mroDateFieldCalculation ?? "",
            cmoReadReceipts: base?.cmoReadReceipts ?? false,
            dispatchDuration: base?.dispatchDuration ?? "",
            poAcknowledgementAvailable: base?.poAcknowledgementAvailable ?? true,
        };
    });

    // API base URL - replace with your actual API base URL
    const apiBaseUrl = useMemo(() => {
        const appName = localStorage.getItem("appName");
        const network = localStorage.getItem("network");
        const collabType = localStorage.getItem("collabType");
        const roleName = localStorage.getItem("roleName");

        if (appName && network && collabType && roleName) {
            return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
        }
        return null;
    }, [
        localStorage.getItem("appName"),
        localStorage.getItem("network"),
        localStorage.getItem("collabType"),
        localStorage.getItem("roleName"),
    ]);

    const viewOnlyProductDetails = location.state?.viewProductDetails;



    const editPendingStatus = location.state?.isPending && location.state?.isEdit;
    useEffect(() => {
        let isMounted = true; // Add isMounted flag
        const collabrixProductID = formData?.productDetails?.collabrixProductID;
        if (editPendingStatus && collabrixProductID) {
            const callData = async () => {
                try {
                    const response = await axios.get(
                        `${apiBaseUrl}/fetchProductDetails/${collabrixProductID}`
                    );
                    if (isMounted && response.data && response.data.status) {
                        const apiData = response.data.data;
                        setUpdateModeData(apiData);
                        const otherConfig = apiData.otherConfiguration || {};
                        setStepData(prevData => {
                            const updatedData = {
                                ...prevData,
                                mroDateFieldCalculation: otherConfig.mroDateFieldCalculation || prevData.mroDateFieldCalculation,
                                cmoReadReceipts: otherConfig.cmoReadReceipts ?? false,
                                dispatchDuration: otherConfig.dispatchDuration || prevData.dispatchDuration,
                                poAcknowledgementAvailable: otherConfig.poAcknowledgementAvailable ?? true,
                            };
                            if (typeof updateStepData === "function") {
                                updateStepData("otherConfiguration", updatedData);
                            }
                            return updatedData;
                        });
                    } else if (isMounted) {
                        setErrors({});
                    }
                } catch (error) {
                    if (isMounted) setErrors({});
                }
            };
            if (!readOnly) {
                callData();
            }
        }
        return () => { isMounted = false; };
    }, [editPendingStatus, formData?.productDetails?.collabrixProductID]);
    // Update stepData when formData changes (like when navigating back to this step)
    // At the top of OtherConfigurations
    

    useEffect(() => {
        if (formData && formData?.otherConfiguration) {
            const updatedData = {
                ...formData?.otherConfiguration,
                mroDateFieldCalculation: formData?.otherConfiguration?.mroDateFieldCalculation ?? "",
                cmoReadReceipts: formData?.otherConfiguration?.cmoReadReceipts ?? false,
                dispatchDuration: formData?.otherConfiguration?.dispatchDuration ?? "",
                poAcknowledgementAvailable: formData?.otherConfiguration?.poAcknowledgementAvailable ?? true,
            };
            setStepData(updatedData);
        }
    }, [formData]);
    // Handle changes to form fields
    const handleChange = (event) => {
        const { name, value } = event.target;
        let safeValue = value;
        // Convert string 'true'/'false' to boolean for boolean fields
        if (["cmoReadReceipts", "poAcknowledgementAvailable"].includes(name)) {
            safeValue = value === "true" || value === true;
        }
        const updatedStepData = {
            ...stepData,
            [name]: safeValue,
        };
        setStepData(updatedStepData);

        // Update parent step data
        if (typeof updateStepData === "function") {
            updateStepData("otherConfiguration", updatedStepData);
        }

        // Real-time validation
        const newErrors = { ...errors };
        if (name === "mroDateFieldCalculation") {
            if (!safeValue) {
                newErrors.mroDateFieldCalculation = "MRO date fields calculation is required";
            } else if (isNaN(safeValue)) {
                newErrors.mroDateFieldCalculation = "MRO date fields must be a number";
            } else {
                delete newErrors.mroDateFieldCalculation;
            }
        }
        if (name === "dispatchDuration") {
            if (!safeValue) {
                newErrors.dispatchDuration = "Dispatch duration is required";
            } else if (isNaN(safeValue)) {
                newErrors.dispatchDuration = "Dispatch duration must be a number";
            } else {
                delete newErrors.dispatchDuration;
            }
        }
        setErrors(newErrors);
    };

    // State for loading and error handling
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "error",
        key: Date.now(),
    });

    // Handle closing snackbar
    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar(prev => ({ ...prev, open: false }));
    };
    const validateForm = () => {
        let isValid = true;
        const newErrors = {};

        if (!stepData.mroDateFieldCalculation) {
            newErrors.mroDateFieldCalculation = "MRO date fields calculation is required";
            isValid = false;
        } else if (isNaN(stepData.mroDateFieldCalculation)) {
            newErrors.mroDateFieldCalculation = "MRO date fields must be a number";
            isValid = false;
        }

        if (!stepData.dispatchDuration) {
            newErrors.dispatchDuration = "Dispatch duration is required";
            isValid = false;
        } else if (isNaN(stepData.dispatchDuration)) {
            newErrors.dispatchDuration = "Dispatch duration must be a number";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const validate = () => {
        let isValid = true;
        if (stepData.mroDateFieldCalculation === "" || isNaN(stepData.mroDateFieldCalculation)) isValid = false;
        if (stepData.dispatchDuration === "" || isNaN(stepData.dispatchDuration)) isValid = false;
        return isValid;
    };

    // Call validation on every stepData change
    useEffect(() => {
        onValidationChange(validate());
    }, [stepData, onValidationChange]);

    const saveOtherConfigurations = async () => {
        // Validate form since there are changes
        if (!validateForm()) {
            setSnackbar({
                open: true,
                message: "Please correct the errors before proceeding.",
                severity: "error",
            });
            return;
        }

        try {
            setIsLoading(true);
            setErrors({});
            // Prepare the payload with the mapped field names

            const productConfigFormData = sessionStorage.getItem("productConfigFormData");
            const parsedFormData = productConfigFormData ? JSON.parse(productConfigFormData) : {};
            const payload = {
                updateOtherConfigFlag: true,
                productDetails: {
                    vendorMosymphonyID: parsedFormData?.productDetails?.vendorMosymphonyID,
                    collabrixProductID: parsedFormData?.productDetails?.collabrixProductID,
                    pendingConfiguration: true,
                },
                otherConfiguration: {
                    mroDateFieldCalculation: Number(stepData.mroDateFieldCalculation),
                    cmoReadReceipts: stepData.cmoReadReceipts,
                    dispatchDuration: Number(stepData.dispatchDuration),
                    poAcknowledgementAvailable: stepData.poAcknowledgementAvailable,
                }
            };

            // Make API call to save other configurations
            const response = await axios.post(
                `${apiBaseUrl}/updateProductDetails`,
                payload
            );

            // Mark API call as successful
            if (typeof markApiCallSuccessful === 'function' && response.data && response.data.status) {
                markApiCallSuccessful();
                return;
            }

            if (response.data && response.data.status) {
                // This section will run only if markApiCallSuccessful is not available
                handleNext();
                setSnackbar({
                    open: true,
                    message: "Other configurations saved successfully!",
                    severity: "success",
                });
            } else {
                setSnackbar({
                    open: true,
                    message: "Failed to save other configurations. Please try again.",
                    severity: "error",
                });
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: "An error occurred while saving: " + (error.message || "Unknown error"),
                severity: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <form>
            <Paper elevation={0} className={classes.paper}>
                <Typography variant="h6">Other Configurations</Typography>
                <Grid container spacing={3} className="two-col-forms-grid">
                    {/* MRO date fields calculation */}
                    <Grid item xs={12} md={6}>
                        <FormControl className={classes.formControl}>
                            <Typography component="label" className={classes.label}>
                                MRO date fields calculation (days)
                                {!readOnly && <span className={classes.requiredField}>*</span>}
                            </Typography>
                            {!readOnly ? (
                                <TextField
                                    value={stepData?.mroDateFieldCalculation}
                                    onChange={handleChange}
                                    name="mroDateFieldCalculation"
                                    variant="outlined"
                                    className={classes.textField}
                                    error={!!errors.mroDateFieldCalculation}
                                    helperText={errors.mroDateFieldCalculation}
                                    placeholder="Enter text here"
                                    InputProps={{
                                        style: commonInlineStyles.input,
                                    }}
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, "");
                                    }}
                                    inputProps={{ ...commonInlineStyles.inputProps, inputMode: 'numeric', pattern: '[0-9]*' }} style={commonInlineStyles.input}
                                    sx={inputFieldStyles}
                                />
                            ) : (
                                <Typography>{stepData?.mroDateFieldCalculation || "N/A"}</Typography>
                            )}
                        </FormControl>
                    </Grid>

                    {/* CMO read receipts */}
                    <Grid item xs={12} md={6}>
                        <FormControl className={classes.formControl}>
                            <Typography component="label" className={classes.label}>
                                CMO read receipts
                            </Typography>
                            {!readOnly ? (
                                <Select
                                    value={stepData?.cmoReadReceipts}
                                    onChange={handleChange}
                                    name="cmoReadReceipts"
                                    variant="outlined"
                                    className={classes.select}
                                    MenuProps={{
                                        anchorOrigin: {
                                            vertical: "bottom",
                                            horizontal: "left",
                                        },
                                        transformOrigin: {
                                            vertical: "top",
                                            horizontal: "left",
                                        },
                                        getContentAnchorEl: null,
                                    }}
                                >
                                    <MenuItem value={true}>YES</MenuItem>
                                    <MenuItem value={false}>NO</MenuItem>
                                </Select>
                            ) : (
                                <Typography>{stepData?.cmoReadReceipts ? "YES" : "NO"}</Typography>
                            )}
                        </FormControl>
                    </Grid>

                    {/* Product type is Finished Goods (FG) */}
                    <Grid item xs={12} md={6}>
                        <FormControl className={classes.formControl}>
                            <Typography component="label" className={classes.label}>
                                Product type is Finished Goods (FG)
                            </Typography>
                            <Typography>
                                {formData?.productDetails?.productTypeName
                                    === "Finished Goods (FG)" ? "YES (Market order)" : "NO (Order acknowledgement)"}
                            </Typography>
                        </FormControl>
                    </Grid>

                    {/* Duration between ready for dispatch and dispatched */}
                    <Grid item xs={12} md={6}>
                        <FormControl className={classes.formControl}>
                            <Typography component="label" className={classes.label}>
                                Duration between ready for dispatch and dispatched (days)
                                {!readOnly && <span className={classes.requiredField}>*</span>}
                            </Typography>
                            {!readOnly ? (
                                <TextField
                                    value={stepData?.dispatchDuration}
                                    onChange={handleChange}
                                    name="dispatchDuration"
                                    variant="outlined"
                                    error={!!errors.dispatchDuration}
                                    helperText={errors.dispatchDuration}
                                    sx={inputFieldStyles}
                                    inputProps={{ ...commonInlineStyles.inputProps, inputMode: 'numeric', pattern: '[0-9]*' }}
                                    style={commonInlineStyles.input}
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, "");
                                    }}
                                    placeholder="Enter text here"
                                />
                            ) : (
                                <Typography>{stepData?.dispatchDuration || "N/A"}</Typography>
                            )}
                        </FormControl>
                    </Grid>

                    {/* PO acknowledgement by CMO required */}
                    <Grid item xs={12} md={6}>
                        <FormControl className={classes.formControl}>
                            <Typography component="label" className={classes.label}>
                                PO acknowledgement by CMO required
                                {!readOnly && <span className={classes.requiredField}>*</span>}
                            </Typography>
                            {!readOnly ? (
                                <Select
                                    value={stepData?.poAcknowledgementAvailable}
                                    onChange={handleChange}
                                    name="poAcknowledgementAvailable"
                                    variant="outlined"
                                    className={classes.select}
                                >
                                    <MenuItem value={true}>YES</MenuItem>
                                    <MenuItem value={false}>NO</MenuItem>
                                </Select>
                            ) : (
                                <Typography>{stepData?.poAcknowledgementAvailable ? "YES" : "NO"}</Typography>
                            )}
                        </FormControl>
                    </Grid>
                </Grid>{" "}
            </Paper>

            {/* Navigation Buttons */}
            {(!readOnly && !viewOnlyProductDetails && !isOtherConfigEditMode) &&
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: activeStep === 0 ? "center" : "space-between",
                    }}
                    className="stepper-navigation-btns"
                    mt={4}
                >
                    {activeStep !== 0 && (
                        <>            <Button
                            variant="outlined"
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            className={classes.button}
                        >
                            Back
                        </Button>
                            <Box sx={{ flex: "1 1 auto" }} />
                        </>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={activeStep === stepsLength - 1 ? handleSubmit : saveOtherConfigurations}
                        className={classes.button}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : activeStep === stepsLength - 1 ? (
                            "Save"
                        ) : (
                            "Save & Next"
                        )}
                    </Button>
                </Box>
            }

            {/* Error Snackbar */}
            <Snackbar
                key={snackbar.key}
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                style={{ zIndex: 9999 }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    style={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </form>
    );
};

export default OtherConfigurations;
